/**
 * Copyright (c) 2019, Tidepool Project
 *
 * This program is free software; you can redistribute it and/or modify it under
 * the terms of the associated License, which is identical to the BSD 2-Clause
 * License as published by the Open Source Initiative at opensource.org.
 *
 * This program is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS
 * FOR A PARTICULAR PURPOSE. See the License for more details.
 *
 * You should have received a copy of the License along with this program; if
 * not, you can obtain one from Tidepool Project at tidepool.org.
 */

import React, { useEffect } from 'react';
import { Container, Typography, Button, Paper, Checkbox, FormControlLabel } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CopyPaste from './CopyPaste.js';
import * as _ from '../../../data/constants.js';
var utils = require('../../../Utils.js');
var clipboard = require('clipboard');

const useStyles = makeStyles(theme => ({
  box: {
    'margin-bottom': '24px',
    'margin-top': '24px',
    'margin-left': '12px',
    'margin-right': '12px',
    padding: '5px',
    display: 'flex',
    'align-items': 'center'
  },
  titles: {
    'margin-left': '24px'
  },
  biggerTitles: {
    'margin-left': '12px',
    'margin-bottom': '24px',
    'margin-top': '12px'
  },
  border: {
    'border-radius': '10px',
    border: '2px solid #EDEDED',
    'padding-left': '48px',
    'padding-right': '48px',
    'padding-top': '24px',
    'padding-bottom': '24px',
    margin: '24px'
  },
  aligned: {
    width: '70%',
    display: 'flex',
    flexDirection: 'column',
    'margin-left': 'auto',
    'margin-right': 'auto',
    'align-items': 'center'
  },
  segments: {
    'margin-bottom': '24px',
    'margin-top': '24px'
  },
  copyText: {
    position: 'absolute',
    left: '-5000px',
    top: '10px'
  }
}));

const TimeValPrintouts = props => {
  const classes = useStyles();

  if (props.dissapear) {
    return <div></div>;
  }

  return (
    <div className={classes.segments}>
      <Typography variant="subtitle1" color="textSecondary">
        {props.title}:
      </Typography>
      {props.value &&
        props.value.map((range, index) => (
          <Typography key={index} variant="subtitle1" color="textSecondary">
            {utils.fixTime(range.time)} : {range.val} {props.units}
          </Typography>
        ))}
    </div>
  );
};

const CorrectionRangePrintOut = props => {
  const classes = useStyles();

  return (
    <div className={classes.segments}>
      <Typography variant="subtitle1" color="textSecondary">
        Correction Range:
      </Typography>
      {props.value &&
        props.value.map((range, index) => (
          <Typography key={index} variant="subtitle1" color="textSecondary">
            {utils.fixTime(range.time)} : {range.lowerLimit} mg/dL - {range.upperLimit} mg/dL
          </Typography>
        ))}
    </div>
  );
};

const SuspendThresholdPrintout = props => {
  const classes = useStyles();

  return (
    <div className={classes.segments}>
      <Typography variant="subtitle1" color="textSecondary">
        Suspend Threshold:
      </Typography>
      <Typography variant="subtitle1" color="textSecondary">
        {props.value} mg/dL
      </Typography>
    </div>
  );
};

const InsulinModelPrintout = props => {
  const classes = useStyles();

  return (
    <div className={classes.segments}>
      <Typography variant="subtitle1" color="textSecondary">
        Insulin Model:
      </Typography>
      <Typography variant="subtitle1" color="textSecondary">
        {props.value === 'rapidAdult' ? 'Rapid acting adult' : 'Rapid acting child'}
      </Typography>
    </div>
  );
};

const DeliveryLimitsPrintout = props => {
  const classes = useStyles();

  return (
    <div className={classes.segments}>
      {props.includeBolus ? (
        <div>
          <Typography variant="subtitle1" color="textSecondary">
            Delivery Limits:
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Max Basal: {props.suspendOnly ? '100%' : props.maxBasal + ' U/hr'}
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Max Bolus: {props.maxBolus} U
          </Typography>
        </div>
      ) : (
        <div>
          <Typography variant="subtitle1" color="textSecondary">
            Max Basal:
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            {props.suspendOnly ? '100%' : props.maxBasal + ' U/hr'}
          </Typography>
        </div>
      )}
    </div>
  );
};

const MainBody = props => {
  const classes = useStyles();

  // if cpt then dont show anything because the cpt will determine settings
  if (props.values.therapySetting === 'cpt') {
    return <div></div>;
  } else {
    return (
      <div>
        <div>
          <div className={classes.segments}>
            <Typography variant="subtitle1" color="textSecondary">
              Training Required:
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              {props.values.training === '' ? 'In-person training required' : 'Online module training'}
            </Typography>
          </div>
          <CorrectionRangePrintOut value={props.values.correctionRange} />
          <SuspendThresholdPrintout value={props.values.suspendThreshold} />
          <InsulinModelPrintout value={props.values.insulinModel} />
          <DeliveryLimitsPrintout
            maxBasal={props.values.maxBasal}
            maxBolus={props.values.maxBolus}
            includeBolus={props.values.therapySetting === 'initial'}
            suspendOnly={props.values.loopMode === 'som'}
          />
          <TimeValPrintouts
            title="Basal Rates"
            value={props.values.basalArray}
            units="U/hr"
            dissapear={props.values.therapySetting !== 'initial'}
          />
          <TimeValPrintouts
            title="Insulin to carb ratios"
            value={props.values.bolusArray}
            units="g/U"
            dissapear={props.values.therapySetting !== 'initial'}
          />
          <TimeValPrintouts
            title="Insulin sensitivity factor"
            value={props.values.sensitivityArray}
            units="mg/dL per U"
            dissapear={props.values.therapySetting !== 'initial'}
          />
        </div>
      </div>
    );
  }
};

const TidepoolLoopMode = props => {
  const classes = useStyles();

  return (
    <div className={classes.segments}>
      <Typography variant="subtitle1" color="textSecondary">
        Tidepool Loop Mode:
      </Typography>
      <Typography variant="subtitle1" color="textSecondary">
        {props.values.loopMode === 'som' ? 'Suspend-only' : 'Closed-loop'}
      </Typography>
    </div>
  );
};

const TherapySetting = props => {
  const classes = useStyles();

  if (props.values.therapySetting === 'initial') {
    return <div></div>;
  } else {
    return (
      <div className={classes.segments}>
        <Typography variant="subtitle1" color="textSecondary">
          Initial Therapy Setting:
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          {utils.initialTherapySettingLanguage(props.values.therapySetting, props.values.firstName)}
        </Typography>
      </div>
    );
  }
};

// Top of the form with the basic patient info
const Heading = props => {
  const classes = useStyles();

  return (
    <div className={classes.segments}>
      <Typography variant="subtitle1" color="textSecondary">
        {props.values.firstName} {props.values.lastName}
      </Typography>
      <Typography variant="subtitle1" color="textSecondary">
        Date of Birth: {utils.birthdayFix(props.values.birthday)}
      </Typography>
      <Typography variant="subtitle1" color="textSecondary">
        Email: {props.values.email}
      </Typography>
    </div>
  );
};

// Page 3 of the form, a verification page with an overview of the settings chosen by the provider.
const SettingsVerification = props => {
  const classes = useStyles();
  new clipboard('.btn');

  // scroll to top on open
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <form onSubmit={props.handleSubmit}>
      <Container>
        <div className={classes.aligned}>
          <div>
            <div className={classes.border}>
              <Typography variant="h5" color="textSecondary">
                {props.titleValue ? props.titleValue : 'Tidepool Loop Order Form and Treatment Plan'}
              </Typography>
              <Heading values={props.values} />
              <TherapySetting values={props.values} />
              <TidepoolLoopMode values={props.values} />
              <MainBody values={props.values} />
              <Button variant="outlined" className="btn" data-clipboard-target="#copy">
                Copy To Clipboard
              </Button>
              <pre className={classes.copyText} id="copy">
                <CopyPaste values={props.values} />
              </pre>
            </div>
            {!props.titleValue && (
              <div className={classes.border}>
                <div className={classes.segments}>
                  <Typography variant="h5" color="textSecondary">
                    Tidepool Loop Terms and Conditions
                  </Typography>
                </div>
                <div className={classes.segments}>
                  <Paper elevation={0} style={{ maxHeight: 250, overflow: 'auto' }}>
                    {_.TermsAndConditions.split('*').map((item, key) => {
                      return (
                        <Typography variant="body2" key={key}>
                          {item}
                        </Typography>
                      );
                    })}
                  </Paper>
                </div>
                <div className={classes.segments}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={props.values.acknowledgedTerms}
                        onChange={props.handleChange}
                        value={props.values.acknowledgedTerms}
                        name="acknowledgedTerms"
                        color="primary"
                      />
                    }
                    label={
                      <Typography color="textSecondary">
                        I agree to the Tidepool Loop terms and conditions above
                      </Typography>
                    }
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </Container>
    </form>
  );
};

export default SettingsVerification;
